import { useEffect, useState } from 'react';
import DocumentShell, { DocSection } from '../../shared/documents/DocumentShell';
import PaymentSummary from '../../shared/documents/PaymentSummary';
import { fmtDay, placeOf } from '../../shared/data/common';
import { getFlightBooking } from '../../api/flights';
import { useAuth } from '../../auth/AuthContext';
import { AirlineLogo } from './FlightSummary';

const paxType = { ADULT: 'Adult', CHILD: 'Child', INFANT: 'Infant' };
const name = (t) => [t.title, t.firstName, t.lastName].filter(Boolean).join(' ');
const serviceText = (s) => (s ? s.description : null);

/** "2026-11-05T06:10" → "2026-11-05", for the date each flight departs. */
const dateOf = (value) => (value && /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : null);

/** Ticket numbers, PNRs, seats and meals from the supplier, for bookings made through the live API. */
function useLiveFlight(booking) {
  const { auth } = useAuth();
  const [live, setLive] = useState({ loading: Boolean(booking?.supplierBookingId), details: null });
  const id = booking?.supplierBookingId;
  const email = booking?.contact?.email;
  const token = auth?.accessToken;
  useEffect(() => {
    if (!id) return undefined;
    const controller = new AbortController();
    getFlightBooking(id, email, token, controller.signal)
      .then((details) => setLive({ loading: false, details }))
      .catch((error) => { if (error.name !== 'AbortError') setLive({ loading: false, details: null }); });
    return () => controller.abort();
  }, [id, email, token]);
  return live;
}

function SegmentCard({ segment, airline, cabin, fareName, date, index, total }) {
  const fromCode = segment.fromCode || placeOf(segment.from).code;
  const toCode = segment.toCode || placeOf(segment.to).code;
  return (
    <div className="doc-segment">
      <div className="doc-seg-airline">
        <AirlineLogo airline={airline} size={34} />
        <div>
          <b>{airline.name}</b>
          <span>{segment.flightNo}</span>
          <span>{cabin}{fareName ? ` · ${fareName}` : ''}</span>
        </div>
      </div>
      <div className="doc-seg-point">
        <span className="doc-seg-label">Departs</span>
        <b className="doc-time">{segment.dep}</b>
        <span>{date ? fmtDay(date) : ''}</span>
        <b>{fromCode} · {segment.from}</b>
        <span className="doc-small">{segment.fromAirport || placeOf(segment.from).airport}</span>
      </div>
      <div className="doc-seg-path">
        <span>{segment.duration}</span>
        <span className="doc-seg-line" />
        <span>{total > 1 ? `Flight ${index + 1} of ${total}` : 'Non-stop'}</span>
      </div>
      <div className="doc-seg-point">
        <span className="doc-seg-label">Arrives</span>
        <b className="doc-time">{segment.arr}</b>
        <span>&nbsp;</span>
        <b>{toCode} · {segment.to}</b>
        <span className="doc-small">{segment.toAirport || placeOf(segment.to).airport}</span>
      </div>
    </div>
  );
}

export default function FlightTicket({ booking }) {
  const s = booking.summary;
  const live = useLiveFlight(booking);
  const details = live.details;
  const pnrs = details
    ? [...new Set(details.travellers.flatMap((t) => t.segments.map((x) => x.pnr)).filter(Boolean))].join(', ')
    : booking.ref;
  const international = s.segments.some((seg) => placeOf(seg.from).country && placeOf(seg.from).country !== placeOf(seg.to).country)
    || (placeOf(s.from).country && placeOf(s.to).country && placeOf(s.from).country !== placeOf(s.to).country);

  // Live segments carry full departure times; match them to the booked segments by airport code, else by order.
  const liveSegment = (seg, i) => details?.segments.find((x) => x.from === seg.fromCode && x.to === seg.toCode) || details?.segments[i];
  const segmentDate = (seg, i) => dateOf(liveSegment(seg, i)?.departureTime) || seg.date || (i === 0 ? s.date : null);

  const travellers = details?.travellers.length
    ? details.travellers.map((t) => ({ name: name(t), type: paxType[t.paxType] || t.paxType, segments: t.segments }))
    : booking.travellers.map((t) => ({ name: name(t), type: t.type, segments: [] }));

  const notice = booking.status === 'cancelled'
    ? 'This booking has been cancelled. This e-ticket is not valid for travel.'
    : booking.status === 'pending' || !pnrs || pnrs === 'Pending'
      ? 'The airline is still issuing this ticket. Carry the e-ticket that shows your airline PNR and ticket numbers.'
      : null;

  return (
    <DocumentShell
      title="E-Ticket"
      subtitle={`${s.from} → ${s.to} · ${fmtDay(s.date)}${s.returnDate ? ` – ${fmtDay(s.returnDate)}` : ''}`}
      booking={booking}
      backTo={`/flights/bookings/${booking.id}`}
      references={[
        ['Airline PNR', pnrs && pnrs !== 'Pending' ? pnrs : 'Awaiting airline', true],
        ['Booking ID', booking.id],
        ...(booking.supplierBookingId ? [[`${booking.supplierCode || 'Supplier'} reference`, booking.supplierBookingId]] : []),
      ]}
      barcode={pnrs && pnrs !== 'Pending' ? `${pnrs.split(',')[0].trim()}/${booking.id}` : booking.id}
      notice={notice}
    >
      <DocSection title="Flight details">
        {s.segments.map((seg, i) => (
          <SegmentCard key={`${seg.flightNo}-${i}`} segment={seg} airline={s.airline} cabin={s.cabin} fareName={s.fareName}
            date={segmentDate(seg, i)} index={i} total={s.segments.length} />
        ))}
        {s.segments.filter((seg) => seg.layover).map((seg) => (
          <p key={seg.flightNo} className="doc-small">Layover of {seg.layover} in {seg.from}. Re-check baggage only if your airline asks you to.</p>
        ))}
      </DocSection>

      <DocSection title="Passengers">
        <table className="doc-table">
          <thead>
            <tr><th>#</th><th>Passenger</th><th>Type</th><th>Flight</th><th>PNR</th><th>E-ticket no.</th><th>Seat</th><th>Meal</th><th>Extra baggage</th></tr>
          </thead>
          <tbody>
            {travellers.flatMap((t, i) => (t.segments.length ? t.segments : [null]).map((x, k) => (
              <tr key={`${i}-${k}`}>
                {k === 0 && <td rowSpan={Math.max(1, t.segments.length)}>{i + 1}</td>}
                {k === 0 && <td rowSpan={Math.max(1, t.segments.length)}><b>{t.name}</b></td>}
                {k === 0 && <td rowSpan={Math.max(1, t.segments.length)}>{t.type}</td>}
                <td>{x ? x.segmentKey : s.segments.map((seg) => seg.flightNo).join(', ')}</td>
                <td>{x?.pnr || booking.ref || '—'}</td>
                <td>{x?.ticketNumber || (x ? 'Issuing' : '—')}</td>
                <td>{serviceText(x?.seat) || 'At check-in'}</td>
                <td>{serviceText(x?.meal) || '—'}</td>
                <td>{serviceText(x?.baggage) || '—'}</td>
              </tr>
            )))}
          </tbody>
        </table>
        {live.loading && <p className="doc-small">Loading ticket numbers and seats from the airline…</p>}
      </DocSection>

      <div className="doc-columns">
        <DocSection title="Baggage allowance">
          <table className="doc-table doc-kv">
            <tbody>
              <tr><td>Cabin baggage</td><td>{s.baggage.cabin} per adult</td></tr>
              <tr><td>Check-in baggage</td><td>{s.baggage.checkin} per adult</td></tr>
            </tbody>
          </table>
          <p className="doc-small">Infants usually have no check-in allowance. Extra baggage bought with this booking is listed above.</p>
        </DocSection>
        <PaymentSummary booking={booking} />
      </div>

      <DocSection title="Contact details">
        <table className="doc-table doc-kv">
          <tbody>
            <tr><td>Email</td><td>{booking.contact.email}</td></tr>
            <tr><td>Mobile</td><td>{booking.contact.phone}</td></tr>
            {booking.contact.gstin && <tr><td>GSTIN</td><td>{booking.contact.gstin} · {booking.contact.company}</td></tr>}
          </tbody>
        </table>
      </DocSection>

      <DocSection title="Important information" className="doc-info">
        <ul>
          <li>Carry a valid government photo ID{international ? ' and your passport (with any visa needed)' : ''} for every passenger. Names must match this ticket.</li>
          <li>Check-in counters close {international ? '60' : '45'} minutes before departure. Arrive at least {international ? '3 hours' : '2 hours'} early.</li>
          <li>Web check-in usually opens 48 hours before departure on the airline's website or app.</li>
          <li>All flight times are local to the airport shown. Schedules can change; the airline will inform you of any change.</li>
          <li>To change or cancel, use My Trips or call support. Airline penalties and the {booking.fare.convenienceFee > 0 ? 'convenience fee' : 'service fee'} apply as per the fare rules.</li>
          <li>Booking barcodes identify your booking at the airline counter. They are not boarding passes.</li>
        </ul>
      </DocSection>
    </DocumentShell>
  );
}
